import LoadingTableNormal from "components/shared-components/LoadingTableNormal";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { STATEMENT_TYPE_LABEL } from "../constants";
import { formatMonthLabel, formatMoney, parseInvoiceNumbers } from "../helper";
import FilterBar from "./FilterBar";
import InvoicePdfModal from "./InvoicePdfModal";
import InvoiceTable from "./InvoiceTable";

const StepSelectInvoice = props => {
  const {
    result,
    appliedFilters,
    searched,
    isLoading,
    selectedIds,
    page,
    sizePerPage,
    onSearch,
    onSelect,
    onSelectAll,
    onCreate,
    onPageChange,
    onClearSelection,
    isCreating,
  } = props;

  const { valid, invalid } = result;

  // Mọi hóa đơn trong một bảng kê phải cùng bên mua. Chỉ chặn khi lọc quá
  // rộng — nhập sẵn danh sách số HĐ thì người dùng đã tự khoanh vùng rồi.
  const canSelect =
    Boolean(appliedFilters?.keyword) ||
    parseInvoiceNumbers(appliedFilters?.invoiceNumbers).length > 0;

  const [pdfInvoice, setPdfInvoice] = useState(null);

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <FilterBar onSearch={onSearch} isLoading={isLoading} />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0 font-size-15">Hóa đơn hợp lệ</h5>
              <span className="badge badge-soft-primary rounded-pill font-size-12">
                {valid.total}
              </span>
            </div>
            {selectedIds.length > 0 && (
              <span className="badge badge-soft-success rounded-pill font-size-16 d-inline-flex align-items-center">
                <i className="bx bx-check-circle me-1 align-middle" />
                Đã chọn {selectedIds.length}
                <button
                  type="button"
                  className="btn btn-link p-0 ms-2 text-success text-decoration-none lh-1"
                  title="Bỏ chọn tất cả"
                  aria-label="Bỏ chọn tất cả"
                  onClick={onClearSelection}
                >
                  <i className="bx bx-x font-size-18 align-middle" />
                </button>
              </span>
            )}
          </div>

          {searched && !canSelect && valid.items.length > 0 && (
            <div className="alert alert-warning py-2 px-3 font-size-12 d-flex align-items-center gap-2">
              <i className="bx bx-info-circle font-size-16" />
              <span>
                Chưa chọn bên mua nên không tích chọn được. Một bảng kê chỉ gồm
                hóa đơn của cùng một bên mua — hãy nhập bên mua hoặc danh sách
                số HĐ rồi tìm lại.
              </span>
            </div>
          )}

          {searched && appliedFilters && (
            <div className="d-flex flex-wrap gap-3 text-muted font-size-12 mb-3">
              <span>
                <i className="bx bx-store me-1 align-middle" />
                Bên bán:{" "}
                <span className="text-body fw-bold">
                  {appliedFilters.sellerName || "—"}
                </span>
              </span>
              <span>
                <i className="bx bx-user me-1 align-middle" />
                Bên mua:{" "}
                <span className="text-body fw-bold">
                  {appliedFilters.keyword || "Tất cả"}
                </span>
              </span>
              <span>
                <i className="bx bx-calendar me-1 align-middle" />
                Kỳ hóa đơn:{" "}
                <span className="text-body fw-bold">
                  {formatMonthLabel(appliedFilters.month)}
                </span>
              </span>
              <span>
                <i className="bx bx-transfer-alt me-1 align-middle" />
                Hình thức xử lý:{" "}
                <span className="text-body fw-bold">
                  {STATEMENT_TYPE_LABEL[appliedFilters.type] || ""}
                </span>
              </span>
            </div>
          )}

          {isLoading ? (
            <LoadingTableNormal countColumn="9" countRow="5" />
          ) : !searched ? (
            <div className="text-center text-muted py-4">
              Nhập điều kiện rồi bấm Tìm kiếm để lấy danh sách hóa đơn
            </div>
          ) : (
            <React.Fragment>
              <InvoiceTable
                items={valid.items}
                selectable={canSelect}
                selectedIds={selectedIds}
                onSelect={onSelect}
                onSelectAll={onSelectAll}
                page={page}
                sizePerPage={sizePerPage}
                totalSize={valid.total}
                onPageChange={onPageChange}
                onViewPdf={setPdfInvoice}
                emptyText="Không tìm thấy hóa đơn phù hợp"
              />
              {valid.items.length > 0 && (
                <Row className="mt-2">
                  <Col md="12" className="text-end">
                    <span className="me-4">
                      Tổng tiền chưa thuế:{" "}
                      <strong>{formatMoney(valid.sum_net)}</strong>
                    </span>
                    <span className="me-4">
                      Tổng tiền thuế:{" "}
                      <strong>{formatMoney(valid.sum_vat)}</strong>
                    </span>
                    <span>
                      Tổng thanh toán:{" "}
                      <strong>{formatMoney(valid.sum_pay)}</strong>
                    </span>
                  </Col>
                </Row>
              )}
            </React.Fragment>
          )}
        </CardBody>
      </Card>

      {invalid.total > 0 && (
        <Card>
          <CardBody>
            <div className="d-flex align-items-center gap-2 mb-3">
              <h5 className="mb-0 font-size-15 text-danger">
                Hóa đơn không đủ điều kiện
              </h5>
              <span className="badge badge-soft-danger rounded-pill font-size-12">
                {invalid.total}
              </span>
            </div>
            <InvoiceTable
              items={invalid.items}
              showReason
              page={page}
              sizePerPage={sizePerPage}
              totalSize={invalid.total}
              onPageChange={onPageChange}
            />
          </CardBody>
        </Card>
      )}

      {searched && (
        <React.Fragment>
          <InvoicePdfModal
            isOpen={Boolean(pdfInvoice)}
            toggle={() => setPdfInvoice(null)}
            invoice={pdfInvoice}
          />

          {/* Chừa chỗ để thanh cố định không che mất dòng cuối của bảng */}
          <div style={{ height: 84 }} />
          {/*
            Dùng position fixed chứ không phải sticky: layout ngang bọc nội dung
            trong .main-content vốn có overflow: hidden, sticky sẽ không dính.
          */}
          <div
            className="d-flex justify-content-end align-items-center gap-3 border-top py-3 px-4"
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100,
              background: "var(--bs-body-bg)",
              boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
            }}
          >
            {selectedIds.length > 0 && (
              <span className="text-muted font-size-13">
                Đã chọn {selectedIds.length} hóa đơn
              </span>
            )}
            <Button
              type="button"
              color="primary"
              disabled={selectedIds.length === 0 || isCreating}
              onClick={onCreate}
            >
              {isCreating ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="bx bx-file me-1" />
              )}
              Lập bảng kê
            </Button>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

StepSelectInvoice.propTypes = {
  result: PropTypes.object.isRequired,
  appliedFilters: PropTypes.object,
  searched: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectedIds: PropTypes.array,
  page: PropTypes.number,
  sizePerPage: PropTypes.number,
  onSearch: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  isCreating: PropTypes.bool,
};

StepSelectInvoice.defaultProps = {
  appliedFilters: null,
  searched: false,
  isLoading: false,
  selectedIds: [],
  page: 1,
  sizePerPage: 50,
  isCreating: false,
};

export default StepSelectInvoice;
